import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Currency = 'CFA' | 'EUR' | 'USD' | 'CHF';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatPrice: (priceCFA: number) => string;
  convertPrice: (priceCFA: number, targetCurrency?: Currency) => number;
  getCurrencySymbol: () => string;
  exchangeRates: { EUR: number; USD: number; CHF: number };
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  CFA: 'F CFA',
  EUR: '€',
  USD: '$',
  CHF: 'CHF',
};

export const CURRENCY_LABELS: Record<Currency, string> = {
  CFA: 'Franc CFA',
  EUR: 'Euro',
  USD: 'Dollar US',
  CHF: 'Franc Suisse',
};

// Taux statiques : 1 devise étrangère = X CFA
const STATIC_RATES = {
  EUR: 655.957,
  USD: 600,
  CHF: 700,
};

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    const saved = localStorage.getItem('preferred_currency');
    return (saved as Currency) || 'CFA';
  });

  useEffect(() => {
    localStorage.setItem('preferred_currency', currency);
  }, [currency]);

  const setCurrency = (newCurrency: Currency) => setCurrencyState(newCurrency);

  const convertPrice = (priceCFA: number, targetCurrency?: Currency): number => {
    if (priceCFA === undefined || priceCFA === null || isNaN(priceCFA)) return 0;
    const target = targetCurrency || currency;
    if (target === 'CFA') return priceCFA;
    let converted = 0;
    if (target === 'EUR') converted = priceCFA / STATIC_RATES.EUR;
    else if (target === 'USD') converted = priceCFA / STATIC_RATES.USD;
    else if (target === 'CHF') converted = priceCFA / STATIC_RATES.CHF;
    // Arrondi à la vingtaine supérieure
    return Math.ceil(converted / 20) * 20;
  };

  const formatPrice = (priceCFA: number): string => {
    if (priceCFA === undefined || priceCFA === null || isNaN(priceCFA)) return '—';
    const price = convertPrice(priceCFA);
    const symbol = CURRENCY_SYMBOLS[currency];
    if (currency === 'CFA') return `${price.toLocaleString('fr-FR')} ${symbol}`;
    if (currency === 'EUR') return `${price.toLocaleString('fr-FR')} ${symbol}`;
    return `${symbol}${price.toLocaleString('en-US')}`;
  };

  const getCurrencySymbol = () => CURRENCY_SYMBOLS[currency];

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        formatPrice,
        convertPrice,
        getCurrencySymbol,
        exchangeRates: STATIC_RATES,
        loading: false,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within a CurrencyProvider');
  return context;
}
